import type { FormEvent } from 'react'
import type { DemoAnswer } from '@/lib/ai-demo'

import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { ArrowUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatCurrency } from '@/lib/format'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { LogoMark } from '@/components/generals/logo'
import { TypingDots } from '@/components/landing/ai/chat-preview'

import {
    answerQuestion,
    HOUSEHOLD,
    MONTHLY_FLOW,
    SUGGESTED_QUESTIONS
} from '@/lib/ai-demo'

interface ChatMessage {
    id: number
    role: 'user' | 'assistant'
    paragraphs: Array<string>
    followUps?: Array<string>
}

const WELCOME: ChatMessage = {
    id: 0,
    role: 'assistant',
    paragraphs: [
        'Hello, this is the Verdane AI.',
        `For this demo I am looking after ${HOUSEHOLD.name}, ${HOUSEHOLD.age}, a product designer in ${HOUSEHOLD.city}. Ask whatever you like about her finances, or pick a question below.`
    ]
}

interface StreamingTextProps {
    paragraphs: Array<string>
    active: boolean
    onTick: () => void
    onDone: () => void
}

const Paragraph = ({ text, first }: { text: string, first: boolean }) => {
    if (text.startsWith('• ')) {
        return (
            <p className='relative pl-4'>
                <span aria-hidden='true' className='absolute left-0 top-[0.6em] size-1 rounded-full bg-citron' />
                {text.slice(2)}
            </p>
        )
    }

    return <p className={cn(first && 'font-medium text-cream')}>{text}</p>
}

/** Reveals an answer word by word, like a reply being written. */
const StreamingText = ({ paragraphs, active, onTick, onDone }: StreamingTextProps) => {
    const reduced = useReducedMotion()
    const words = useMemo(() => paragraphs.map(paragraph => paragraph.split(' ')), [paragraphs])
    const total = useMemo(() => words.reduce((sum, list) => sum + list.length, 0), [words])
    const [count, setCount] = useState(active ? 0 : total)

    useEffect(() => {
        if (!active) return

        if (reduced) {
            setCount(total)
            return
        }

        const interval = setInterval(() => {
            setCount(current => {
                const next = Math.min(total, current + 1)

                if (next >= total) clearInterval(interval)

                return next
            })
        }, 32)

        return () => clearInterval(interval)
    }, [active, reduced, total])

    useEffect(() => {
        if (!active) return

        onTick()

        if (count >= total) onDone()
    }, [active, count, total, onTick, onDone])

    let remaining = count

    return (
        <div className='space-y-2'>
            {words.map((list, index) => {
                if (remaining <= 0) return null

                const visible = list.slice(0, remaining).join(' ')

                remaining -= list.length

                return <Paragraph key={index} text={visible} first={index === 0} />
            })}
        </div>
    )
}

interface AiDemoProps {
    incoming?: { text: string, nonce: number } | null
}

export const AiDemo = ({ incoming }: AiDemoProps) => {
    const [messages, setMessages] = useState<Array<ChatMessage>>([WELCOME])
    const [input, setInput] = useState('')
    const [thinking, setThinking] = useState(false)
    const [streamingId, setStreamingId] = useState<number | null>(null)
    const nextId = useRef(1)
    const timers = useRef<Array<ReturnType<typeof setTimeout>>>([])
    const scrollRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const busy = thinking || streamingId !== null

    const scrollToEnd = useCallback(() => {
        const node = scrollRef.current

        if (node) node.scrollTop = node.scrollHeight
    }, [])

    const finishStreaming = useCallback(() => setStreamingId(null), [])

    const ask = useCallback((text: string) => {
        const question = text.trim().slice(0, 300)

        if (!question || busy) return

        const answer: DemoAnswer = answerQuestion(question)

        setMessages(current => [...current, { id: nextId.current++, role: 'user', paragraphs: [question] }])
        setInput('')
        setThinking(true)

        timers.current.push(setTimeout(() => {
            const id = nextId.current++

            setThinking(false)
            setMessages(current => [...current, { id, role: 'assistant', paragraphs: answer.paragraphs, followUps: answer.followUps }])
            setStreamingId(id)
        }, 700 + Math.round(Math.random() * 500)))
    }, [busy])

    useEffect(() => {
        const pending = timers.current

        return () => pending.forEach(timer => clearTimeout(timer))
    }, [])

    useEffect(() => {
        scrollToEnd()
    }, [messages, thinking, scrollToEnd])

    const lastNonce = useRef<number | null>(null)

    useEffect(() => {
        if (!incoming || incoming.nonce === lastNonce.current || busy) return

        lastNonce.current = incoming.nonce
        ask(incoming.text)
    }, [incoming, ask, busy])

    const submit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        ask(input)
    }

    const lastAssistant = [...messages].reverse().find(message => message.role === 'assistant')
    const followUps = !busy && lastAssistant?.followUps ? lastAssistant.followUps : SUGGESTED_QUESTIONS.slice(0, 4)

    return (
        <div className='grid gap-4 lg:grid-cols-[340px_minmax(0,1fr)]'>
            <aside aria-label='Example household' className='glass-dark grain relative overflow-hidden rounded-[30px] p-6'>
                <p className='text-[11px] font-medium uppercase tracking-[0.28em] text-cream/50'>
                    Example household
                </p>

                <p className='mt-4 font-serif text-4xl italic leading-none text-cream'>
                    {HOUSEHOLD.name}, {HOUSEHOLD.age}
                </p>

                <p className='mt-2 text-sm text-cream/55'>
                    {HOUSEHOLD.role}, {HOUSEHOLD.city}
                </p>

                <dl className='mt-7 space-y-2.5 border-t border-white/10 pt-5 text-[13px]'>
                    {MONTHLY_FLOW.map((item, index) => (
                        <div key={item.label} className={cn('flex items-center justify-between gap-3', index === MONTHLY_FLOW.length - 1 && 'border-t border-white/10 pt-2.5')}>
                            <dt className='text-cream/60'>
                                {item.label}
                            </dt>

                            <dd className={cn('tabular-nums', index === MONTHLY_FLOW.length - 1 ? 'font-medium text-citron' : 'text-cream/85')}>
                                {formatCurrency(item.amount)}
                            </dd>
                        </div>
                    ))}
                </dl>

                <dl className='mt-6 grid grid-cols-2 gap-2 text-[12px]'>
                    {HOUSEHOLD.balances.map(item => (
                        <div key={item.label} className='rounded-2xl bg-white/[0.04] px-3 py-2.5'>
                            <dt className='text-cream/45'>
                                {item.label}
                            </dt>

                            <dd className={cn('mt-0.5 tabular-nums', item.amount < 0 ? 'text-cream/60' : 'text-cream')}>
                                {formatCurrency(item.amount)}
                            </dd>
                        </div>
                    ))}
                </dl>

                <p className='mt-6 text-xs leading-relaxed text-cream/40'>
                    Sample figures only. Once you join, the advisor works from your own linked accounts.
                </p>
            </aside>

            <div className='glass-dark grain relative flex h-[640px] flex-col overflow-hidden rounded-[30px]'>
                <div className='flex items-center justify-between gap-3 border-b border-white/[0.07] px-5 py-4 sm:px-6'>
                    <div className='flex items-center gap-2.5'>
                        <span className='grid size-8 place-items-center rounded-full bg-white/[0.06]'>
                            <LogoMark className='size-4 text-cream' />
                        </span>

                        <span>
                            <span className='block text-sm text-cream'>
                                Verdane AI
                            </span>

                            <span className='flex items-center gap-1.5 text-[11px] text-cream/45'>
                                <span className='size-1.5 rounded-full bg-citron shadow-[0_0_8px_rgba(226,243,122,0.9)]' />
                                {busy ? 'Thinking it through' : 'Available'}
                            </span>
                        </span>
                    </div>

                    <button
                        type='button'
                        onClick={() => {
                            setMessages([WELCOME])
                            setStreamingId(null)
                            setThinking(false)
                        }}
                        disabled={busy || messages.length === 1}
                        className='rounded-full px-3 py-1.5 text-xs text-cream/55 transition-colors hover:bg-white/5 hover:text-cream disabled:opacity-30'
                    >
                        Start over
                    </button>
                </div>

                <div ref={scrollRef} className='flex-1 space-y-4 overflow-y-auto px-4 py-5 sm:px-6' aria-live='polite' aria-busy={busy}>
                    {messages.map(message => (
                        message.role === 'user'
                            ? (
                                <div key={message.id} className='ml-auto max-w-[85%] rounded-[20px] rounded-br-md bg-cream px-4 py-3 text-[14px] leading-snug text-black'>
                                    {message.paragraphs[0]}
                                </div>
                            )
                            : (
                                <div key={message.id} className='mr-auto max-w-[92%] rounded-[20px] rounded-bl-md bg-white/[0.05] px-4 py-3.5 text-[14px] leading-relaxed text-cream/80 ring-1 ring-white/[0.06]'>
                                    {message.id === streamingId
                                        ? <StreamingText paragraphs={message.paragraphs} active onTick={scrollToEnd} onDone={finishStreaming} />
                                        : (
                                            <div className='space-y-2'>
                                                {message.paragraphs.map((paragraph, index) => <Paragraph key={index} text={paragraph} first={index === 0} />)}
                                            </div>
                                        )}
                                </div>
                            )
                    ))}

                    {thinking && (
                        <div className='mr-auto rounded-[20px] rounded-bl-md bg-white/[0.05] px-4 py-3 ring-1 ring-white/[0.06]'>
                            <TypingDots />
                        </div>
                    )}
                </div>

                <div className='border-t border-white/[0.07] p-3 sm:p-4'>
                    <div className='no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1 pb-3'>
                        {followUps.map(question => (
                            <button
                                key={question}
                                type='button'
                                onClick={() => ask(question)}
                                disabled={busy}
                                className='shrink-0 rounded-full bg-white/[0.05] px-3.5 py-2 text-xs text-cream/75 ring-1 ring-white/10 transition-colors hover:bg-white/10 hover:text-cream disabled:opacity-40'
                            >
                                {question}
                            </button>
                        ))}
                    </div>

                    <form onSubmit={submit} className='flex items-center gap-2 rounded-full bg-white/[0.06] p-1.5 pl-5 ring-1 ring-white/10 focus-within:ring-citron/50'>
                        <label htmlFor='ai-demo-input' className='sr-only'>
                            Type a question about money
                        </label>

                        <input
                            ref={inputRef}
                            id='ai-demo-input'
                            type='text'
                            value={input}
                            onChange={event => setInput(event.target.value)}
                            placeholder={"Ask about Maya's finances"}
                            maxLength={300}
                            autoComplete='off'
                            className='h-10 min-w-0 flex-1 bg-transparent text-[15px] text-cream placeholder:text-cream/35 focus:outline-none'
                        />

                        <button
                            type='submit'
                            disabled={busy || !input.trim()}
                            aria-label='Send your question'
                            className='grid size-10 shrink-0 place-items-center rounded-full bg-cream text-black transition-all duration-300 hover:bg-white disabled:opacity-30'
                        >
                            <ArrowUp className='size-4' strokeWidth={2.4} />
                        </button>
                    </form>
                </div>
            </div>
        </div>
    )
}
